import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4B6651] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#5C5F5C] mb-8">Last updated: March 28, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-[#2A2C2A]">
          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">1. Introduction</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              VetNeuroHelp ("we," "our," or "us"), operated from Ellijay, GA, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and share your information when you use our veterinary neurological diagnostic platform.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">2. Information We Collect</h2>
            <h3 className="text-base font-medium text-[#2A2C2A] mt-3">Account Information</h3>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li>Name, email address, and password</li>
              <li>Veterinary license number and issuing entity</li>
              <li>Clinic name and address (if applicable)</li>
            </ul>
            <h3 className="text-base font-medium text-[#2A2C2A] mt-3">Patient & Clinical Data</h3>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li>Animal patient records (name, species, breed, age, weight, sex, owner name)</li>
              <li>Neurological examination findings</li>
              <li>Medical images (JPEG, PNG, DICOM files)</li>
              <li>AI-generated analyses and diagnostic plans</li>
              <li>Treatment records and follow-up data</li>
            </ul>
            <h3 className="text-base font-medium text-[#2A2C2A] mt-3">Usage & Technical Data</h3>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li>IP address, browser type, and device information</li>
              <li>Pages visited and features used</li>
              <li>Cookies and session tokens for authentication</li>
            </ul>
            <h3 className="text-base font-medium text-[#2A2C2A] mt-3">Payment Information</h3>
            <p className="text-[#5C5F5C] leading-relaxed mt-2">
              Payment details (credit card numbers, billing addresses) are processed directly by Stripe and are never stored on our servers. We only retain transaction IDs and subscription status.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-3">
              <li>To provide and improve the Service, including AI-powered neurological analysis</li>
              <li>To authenticate your identity and manage your account</li>
              <li>To process subscription payments and manage billing</li>
              <li>To send transactional emails (welcome, trial reminders, billing confirmations)</li>
              <li>To send follow-up reminders and clinical notifications</li>
              <li>To respond to your support inquiries</li>
              <li>To generate aggregated, anonymized analytics to improve the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">4. Third-Party Data Sharing</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We share data with the following third-party services strictly as required to operate the Service:
            </p>
            <div className="mt-3 space-y-3">
              <div className="bg-[#F8F7F4] p-4 rounded-lg">
                <p className="font-medium text-[#2A2C2A] text-sm">OpenAI (GPT-5.2)</p>
                <p className="text-[#5C5F5C] text-sm mt-1">
                  Clinical data (exam findings, patient info) is sent to OpenAI for AI analysis. Data is processed under <a href="https://openai.com/policies/api-data-usage-policies" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">OpenAI's API Data Usage Policy</a>. OpenAI does not use API data to train its models.
                </p>
              </div>
              <div className="bg-[#F8F7F4] p-4 rounded-lg">
                <p className="font-medium text-[#2A2C2A] text-sm">Stripe</p>
                <p className="text-[#5C5F5C] text-sm mt-1">
                  Payment processing is handled by Stripe under their <a href="https://stripe.com/privacy" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. We do not store credit card numbers.
                </p>
              </div>
              <div className="bg-[#F8F7F4] p-4 rounded-lg">
                <p className="font-medium text-[#2A2C2A] text-sm">Twilio SendGrid</p>
                <p className="text-[#5C5F5C] text-sm mt-1">
                  Email delivery is provided by SendGrid under <a href="https://www.twilio.com/legal/privacy" className="text-[#4B6651] hover:underline" target="_blank" rel="noopener noreferrer">Twilio's Privacy Policy</a>. Only email addresses and message content are shared.
                </p>
              </div>
            </div>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We do not sell, rent, or trade your personal information to any third party for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">5. Cookies & Authentication</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We use essential cookies to maintain your login session. These are httpOnly cookies containing encrypted JWT tokens. We do not use third-party tracking cookies or advertising pixels. No cookie consent banner is required as we only use strictly necessary cookies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">6. Data Storage & Security</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              Your data is stored in encrypted databases hosted on secure cloud infrastructure. We implement industry-standard security measures including:
            </p>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li>Passwords hashed using bcrypt with per-user salting</li>
              <li>HTTPS encryption for all data in transit</li>
              <li>JWT-based authentication with automatic token refresh</li>
              <li>Brute-force login protection with account lockout</li>
              <li>Role-based access control for multi-user clinics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">7. Data Retention</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We retain your account and clinical data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law. Anonymized, aggregated data may be retained indefinitely for analytical purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">8. Your Rights</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">You have the right to:</p>
            <ul className="list-disc pl-6 text-[#5C5F5C] leading-relaxed space-y-1 mt-2">
              <li><strong>Access</strong> your personal data stored in the Service</li>
              <li><strong>Correct</strong> inaccurate information in your account or patient records</li>
              <li><strong>Delete</strong> your account and associated data</li>
              <li><strong>Export</strong> your patient data and case reports (PDF export feature)</li>
              <li><strong>Withdraw consent</strong> for email communications at any time</li>
            </ul>
            <p className="text-[#5C5F5C] leading-relaxed mt-2">
              To exercise any of these rights, contact us at <a href="mailto:helpme@vetneurohelp.com" className="text-[#4B6651] hover:underline">helpme@vetneurohelp.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">9. Veterinary Data & HIPAA</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              VetNeuroHelp processes veterinary medical records, not human medical records. HIPAA (Health Insurance Portability and Accountability Act) applies to human health information and does not directly apply to veterinary records. However, we treat all clinical data with the same level of care and security. If you input any client (pet owner) personal information, you are responsible for obtaining appropriate consent.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">10. Children's Privacy</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              The Service is intended for licensed veterinary professionals and is not directed at individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">11. Changes to This Policy</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. We will notify you via email of any material changes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] border-b border-[#E5E4E0] pb-2">12. Contact</h2>
            <p className="text-[#5C5F5C] leading-relaxed mt-3">
              For privacy-related questions or requests, contact us at:<br />
              <strong>VetNeuroHelp</strong><br />
              Ellijay, GA<br />
              <a href="mailto:helpme@vetneurohelp.com" className="text-[#4B6651] hover:underline">helpme@vetneurohelp.com</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 pb-12 border-t border-[#E5E4E0] flex flex-wrap gap-4 text-sm text-[#5C5F5C]">
          <Link to="/terms" className="hover:text-[#4B6651] hover:underline">Terms of Service</Link>
          <Link to="/contact" className="hover:text-[#4B6651] hover:underline">Contact Us</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
